import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { MAPS_URL, INSTAGRAM_URL } from '../data/constants';
import { MessageCircle, MapPin, Instagram, Zap, Anchor, ArrowUpRight } from 'lucide-react';
export const ContactSection = () => {
  const {
    t
  } = useLanguage();
  const handleWhatsApp = () => {
    const message = t('wa.help');
    window.open(getWhatsAppUrl(message), '_blank');
  };
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <>
      {/* Final CTA Section */}
      <section className="py-14 sm:py-20 md:py-24 bg-[#0A1F44] text-center relative overflow-hidden" data-id="element-71" data-mp="1sshe1jpzn6fk">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-turquoise/20 rounded-full blur-3xl" data-id="element-72" data-mp="1wu309voewx2f" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-gold/10 rounded-full blur-3xl" data-id="element-73" data-mp="7m4whqu53xrn" />

        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeUp} className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-74" data-mp="2nbkk2ui4etl">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-id="element-75" data-mp="cnlzbrw6puav">
            {t('final.title')}
          </h2>
          <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-10 leading-relaxed max-w-2xl mx-auto" data-id="element-76" data-mp="lkwcjiskbtif">
            {t('final.desc')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-77" data-mp="vbqxmju2on2a">
            <button onClick={handleWhatsApp} className="w-full sm:w-auto bg-turquoise hover:bg-turquoise-dark text-navy font-bold py-4 px-8 rounded-full transition-all transform hover:scale-105 flex items-center justify-center space-x-2 uppercase tracking-wide text-sm shadow-[0_0_30px_rgba(0,212,255,0.5)]" data-id="element-78" data-mp="ve3cup8g4jbn">
              <MessageCircle className="w-5 h-5" data-id="element-79" data-mp="6z5j0rbmttxp" />
              <span data-id="element-80" data-mp="6vpz9b70jwpy">{t('final.cta.primary')}</span>
            </button>
            <a href="#barcos" className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white font-bold py-4 px-8 rounded-full transition-all flex items-center justify-center space-x-2 uppercase tracking-wide text-sm backdrop-blur-sm border border-white/10" data-id="element-81" data-mp="ay4m06dszgmi">
              <Anchor className="w-5 h-5" data-id="element-82" data-mp="pmqym0c1pua1" />
              <span data-id="element-83" data-mp="uny8q61nv5jv">{t('final.cta.secondary')}</span>
            </a>
          </div>
        </motion.div>
      </section>

      {/* Contact Block — bigger title, compact dynamic cards */}
      <section id="contacto" className="py-14 sm:py-20 md:py-24 bg-card relative overflow-hidden" data-id="element-84" data-mp="2wf1iyu7n2i3w">
        <div aria-hidden className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0A1F44 1px, transparent 0)',
        backgroundSize: '24px 24px'
      }} data-id="element-85" data-mp="30g3m3m4ofeb5" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-86" data-mp="2j71avjltyev1">
          {/* Huge title */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-8 sm:mb-14" data-id="element-87" data-mp="2f5z9ox8crsqf">
            <span className="inline-block bg-turquoise/10 text-turquoise px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-widest mb-4 sm:mb-6" data-id="element-88" data-mp="2xwgywrtz8671">
              {t('nav.contact')}
            </span>
            <p className="text-lg sm:text-2xl md:text-3xl text-navy font-semibold max-w-2xl mx-auto leading-snug px-2" data-id="element-89" data-mp="1mg0cufnx2rb7">
              {t('final.desc').split('.')[0]}.
            </p>
            <div className="mt-6 w-20 h-1 bg-turquoise mx-auto rounded-full" data-id="element-90" data-mp="3f4pzybhxyhtx" />
          </motion.div>

          {/* Compact action grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-91" data-mp="31ewbvve9g1l9">
            {/* WhatsApp — deep navy blue */}
            <motion.button initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.05
          }} whileHover={{
            y: -4
          }} onClick={handleWhatsApp} className="group bg-[#0A1F44] rounded-2xl p-5 text-left shadow-md hover:shadow-xl transition-all relative overflow-hidden text-white" data-id="element-92" data-mp="35cahze6n9t91">
              <div className="absolute -right-6 -top-6 w-28 h-28 bg-turquoise/30 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" data-id="element-93" data-mp="2555cahche4nq" />
              <div className="relative flex items-start justify-between" data-id="element-94" data-mp="2f4e3x3jvj76x">
                <div data-id="element-95" data-mp="2f2a3wwp3isbx">
                  <div className="w-10 h-10 bg-turquoise rounded-xl flex items-center justify-center mb-3 shadow-md shadow-turquoise/40 group-hover:rotate-6 transition-transform" data-id="element-96" data-mp="2a5t4v7gcytk2">
                    <MessageCircle className="w-5 h-5 text-navy" data-id="element-97" data-mp="1mdmfis91vmu5" />
                  </div>
                  <div className="text-xs font-bold text-turquoise uppercase tracking-wider mb-1" data-id="element-98" data-mp="1hf1amfiusezd">
                    WhatsApp
                  </div>
                  <div className="font-bold text-white text-sm" data-id="element-99" data-mp="1re0lm4r3cmpj">
                    +34 692 489 818
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-turquoise group-hover:rotate-45 transition-all" data-id="element-100" data-mp="13n4m0lge8n7q" />
              </div>
            </motion.button>

            {/* Instagram — bright sky blue */}
            <motion.a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} whileHover={{
            y: -4
          }} className="group bg-[#0EA5E9] rounded-2xl p-5 shadow-md hover:shadow-xl transition-all relative overflow-hidden block text-white" data-id="element-101" data-mp="33rgxpqdug7b8">
              <div className="absolute -right-6 -top-6 w-28 h-28 bg-white/30 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" data-id="element-102" data-mp="450f6rwfonn4" />
              <div className="relative flex items-start justify-between" data-id="element-103" data-mp="95514xg42akq">
                <div data-id="element-104" data-mp="j6h29zii1djm">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-3 shadow-md group-hover:rotate-6 transition-transform" data-id="element-105" data-mp="j937hshx3gyo">
                    <Instagram className="w-5 h-5 text-[#0EA5E9]" data-id="element-106" data-mp="9g4jocu8vug4" />
                  </div>
                  <div className="text-xs font-bold text-white/90 uppercase tracking-wider mb-1" data-id="element-107" data-mp="5essw3zmx36c">
                    Instagram
                  </div>
                  <div className="font-bold text-white text-sm" data-id="element-108" data-mp="eek2jq7lp79">
                    @kevin.boats
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-white/60 group-hover:text-white group-hover:rotate-45 transition-all" data-id="element-109" data-mp="3mp0ksabdl518" />
              </div>
            </motion.a>

            {/* Quick Response */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.15
          }} className="bg-navy rounded-2xl p-5 shadow-md relative overflow-hidden text-white" data-id="element-110" data-mp="1kvkvbgr0xw7u">
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-turquoise/20 rounded-full blur-2xl" data-id="element-111" data-mp="1vc0o01ssqzsl" />
              <div className="relative" data-id="element-112" data-mp="1gf04umoo8d5q">
                <div className="w-10 h-10 bg-turquoise rounded-xl flex items-center justify-center mb-3 shadow-md shadow-turquoise/30" data-id="element-113" data-mp="1gi4fo2wn675d">
                  <Zap className="w-5 h-5 text-navy" data-id="element-114" data-mp="17fv89lwo6tw7" />
                </div>
                <div className="text-xs font-bold text-turquoise uppercase tracking-wider mb-1" data-id="element-115" data-mp="1cdwpe1ajcj51">
                  {t('contact.response').split(' ')[0]}
                </div>
                <div className="font-bold text-white text-sm leading-snug" data-id="element-116" data-mp="31r1i01y9h7wj">
                  {t('contact.response')}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Map Card — Google Maps embed */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100" data-id="element-117" data-mp="3bxuff7dl6xcv">
            <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-118" data-mp="3upx1wzcwt7v1">
              {/* Info */}
              <div className="md:col-span-2 p-6 md:p-8 flex flex-col justify-between" data-id="element-119" data-mp="cftmejz4rzua">
                <div data-id="element-120" data-mp="8h46okqme67h">
                  <div className="flex items-center space-x-2 text-xs font-bold text-turquoise uppercase tracking-wider mb-3" data-id="element-121" data-mp="2e9avls2wt8r5">
                    <MapPin className="w-4 h-4" data-id="element-122" data-mp="2a8h6ivdqdnwo" />
                    <span data-id="element-123" data-mp="2a9sb2xf02zou">{t('boat.location')}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy mb-2 leading-tight" data-id="element-124" data-mp="ye4curz9hqfb">
                    BoatRentalUP
                  </h3>
                  <p className="text-gray-500 text-sm mb-1" data-id="element-125" data-mp="pcej3fhwqfed">
                    Puerto de Torrevieja
                  </p>
                  <p className="text-gray-500 text-sm" data-id="element-126" data-mp="ked2r2bv8u6l">Costa Blanca, España</p>
                </div>
                <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center justify-center space-x-2 bg-navy hover:bg-navy-light text-white font-bold py-3 px-5 rounded-full transition-colors text-xs uppercase tracking-wider self-start" data-id="element-127" data-mp="1i1lh8ddg92z3">
                  <MapPin className="w-4 h-4 text-turquoise" data-id="element-128" data-mp="20rtewfydr17w" />
                  <span data-id="element-129" data-mp="20t4iaenwy6yh">Google Maps</span>
                  <ArrowUpRight className="w-3.5 h-3.5" data-id="element-130" data-mp="1w1czwrewv7jf" />
                </a>
              </div>

              {/* Embedded Map */}
              <div className="md:col-span-3 relative min-h-[260px] md:min-h-[320px]" data-id="element-131" data-mp="23dax5he2fhtt">
                <iframe title="Kevin Boats - BoatRentalUP Torrevieja" src="https://www.google.com/maps?q=BoatRentalUP+Torrevieja&output=embed" loading="lazy" className="absolute inset-0 w-full h-full border-0" referrerPolicy="no-referrer-when-downgrade" allowFullScreen data-id="element-132" data-mp="28aj74up28fjz" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>;
};